import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { CheckCircle, Code, ArrowLeft } from 'lucide-react';
import { gameData, getGameProgress, mockApiCalls, CATEGORY_TO_LOCATION } from '../data/mock';
import useLazyImage from '../hooks/useLazyImage';
import CodingGameSplitLayout from './layouts/CodingGameSplitLayout';
import ResultOverlay from './ui/ResultOverlay';

// Parse function parameters from template string
// Example: "int functionName(int quantity, string name) { ... }"
// Returns: [{"paramName": "quantity", "paramType": "int"}, ...]
const parseFunctionParams = (template) => {
  if (!template) return [];

  // Match function signature: type functionName(params)
  const match = template.match(/\w+\s+\w+\s*\(([^)]*)\)/);
  if (!match || !match[1]) return [];

  const paramsStr = match[1].trim();
  if (!paramsStr) return [];

  // Split by comma and parse each parameter
  const params = [];
  paramsStr.split(',').forEach(param => {
    const trimmed = param.trim();
    if (trimmed) {
      // Format: "type name" e.g., "int quantity"
      const parts = trimmed.split(/\s+/);
      if (parts.length >= 2) {
        params.push({
          paramType: parts[0],
          paramName: parts[1]
        });
      }
    }
  });

  return params;
};

// Parse full function details from template string for header display
// Example: "int functionName(int quantity, string name) { ... }"
// Returns: { functionName: 'functionName', returnType: 'int', args: [{name: 'quantity', type: 'int'}, ...] }
const parseFunctionDetails = (template) => {
  if (!template) return { functionName: '', returnType: 'void', args: [] };

  // Match: [returnType] functionName(params) or function functionName(params)
  const match = template.match(/(?:(\w+)\s+)?(\w+)\s*\(([^)]*)\)/);
  if (!match) return { functionName: '', returnType: 'void', args: [] };

  const returnType = (match[1] && match[1] !== 'function') ? match[1] : 'void';
  const functionName = match[2];
  const paramsStr = match[3] || '';

  const args = [];
  if (paramsStr.trim()) {
    paramsStr.split(',').forEach(param => {
      const parts = param.trim().split(/\s+/);
      if (parts.length >= 2) {
        args.push({
          name: parts[1],
          type: parts[0]
        });
      }
    });
  }

  return { functionName, returnType, args };
};

// Helper to parse validation error messages from API
const parseValidationError = (message) => {
  if (!message) return null;
  try {
    // Try to parse the message as JSON (validation errors come as JSON string)
    const errorObj = JSON.parse(message);
    if (errorObj.line !== undefined && errorObj.message) {
      return {
        line: errorObj.line,
        message: errorObj.message,
      };
    }
  } catch (e) {
    // Not a JSON string, return as-is
  }
  return null;
};

// Format time as HH:MM:SS
const formatTimeRemaining = (ms) => {
  if (ms <= 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const LocationChallenge = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const passedTimeRemaining = routerLocation.state?.timeRemaining;

  const location = gameData.locations.find(loc => loc.id === parseInt(id));
  const [challenge, setChallenge] = useState({});
  const [showEditor, setShowEditor] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState({ capturedDinosaurs: [] });
  const [isCaptured, setIsCaptured] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(passedTimeRemaining ?? null);
  const [showResultOverlay, setShowResultOverlay] = useState(false);
  const [resultOverlayType, setResultOverlayType] = useState(null); // 'success' or 'failure'
  const [overlayTestResults, setOverlayTestResults] = useState(null); // test results for overlay
  const [isTimeEnded, setIsTimeEnded] = useState(
    passedTimeRemaining != null && passedTimeRemaining <= 0
  );
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [maxAttempts, setMaxAttempts] = useState(null);

  const imageUrl = showEditor ? location?.backgroundImage : location?.dinosaurImage;
  const [imageSrc, imageRef] = useLazyImage(imageUrl);

  const editorContainerRef = useRef(null);
  const editorRef = useRef(null);
  const hasFetchedTimeRef = useRef(false);

  // Load progress and challenges on component mount
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load game progress
        const gameProgress = await getGameProgress();
        if (!isMounted) return;

        setProgress(gameProgress);
        setIsCaptured(gameProgress.capturedDinosaurs.includes(parseInt(id)));

        const challenge = gameProgress.questions.find(ch => CATEGORY_TO_LOCATION[ch.category] === location.id) || {};
        setChallenge(challenge);
        setValidationResult(null);

        // Set initial attempts info from challenge
        if (challenge.attemptsLeft != null) {
          setAttemptsLeft(challenge.attemptsLeft);
        }
        // MAX_ATTEMPTS is 2 (from backend)
        setMaxAttempts(2);

        // Fetch time remaining if not already fetched
        if (!hasFetchedTimeRef.current) {
          hasFetchedTimeRef.current = true;
          try {
            const timeResult = await mockApiCalls.getTrackingTime();
            if (isMounted && timeResult.success && timeResult.data) {
              const timeValue = timeResult.data.timeRemaining;
              // API time is source of truth - always use it
              if (timeValue != null) {
                if (timeValue <= 0) {
                  setIsTimeEnded(true);
                  setTimeRemaining(0);
                } else {
                  setTimeRemaining(timeValue);
                }
              }
            }
          } catch (timeError) {
            console.error('Error fetching time:', timeError);
          }
        }

      } catch (error) {
        console.error('Error loading game data:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  // Handle time up
  const handleTimeUp = useCallback(() => {
    navigate('/map');
  }, [navigate]);

  // Track if timer should be running (set once when time becomes available)
  const [isTimerReady, setIsTimerReady] = useState(passedTimeRemaining != null && passedTimeRemaining > 0);

  // Set timer ready when time becomes available from API
  useEffect(() => {
    if (!isTimerReady && timeRemaining != null && timeRemaining > 0) {
      setIsTimerReady(true);
    }
  }, [timeRemaining, isTimerReady]);

  // Countdown timer for pre-editor view
  useEffect(() => {
    // Only run countdown when not in editor view and timer is ready
    if (showEditor || !isTimerReady) {
      return;
    }

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev == null || prev <= 0) {
          return prev;
        }
        if (prev <= 1000) {
          setIsTimeEnded(true);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showEditor, isTimerReady]);

  // Load editor script and track when it's ready
  useEffect(() => {
    // Check if editor is already loaded
    if (window.DRE && window.DRE.initComplete) {
      setIsEditorReady(true);
      return;
    }

    const script = document.createElement('script');
    window.DRE = {};
    window.DRE.folderPath = "/app/ui";

    script.src = "/app/ui/dre/js/includeDelugeEditor_OP.js";
    script.async = true;

    // Check for editor ready state periodically
    const checkEditorReady = setInterval(() => {
      if (window.DRE && window.DRE.initComplete) {
        setIsEditorReady(true);
        clearInterval(checkEditorReady);
      }
    }, 100);

    script.onload = () => {
      // Script loaded, but editor might still be initializing
      // The interval will catch when initComplete is set
    };

    script.onerror = () => {
      console.error('Failed to load editor script');
      clearInterval(checkEditorReady);
    };

    document.body.appendChild(script);

    return () => {
      clearInterval(checkEditorReady);
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (showEditor && editorContainerRef.current && window.DRE && !editorRef.current) {
      const initEditor = async () => {
        // Fetch user ID from Catalyst SDK (stateless)
        let userId = 15841082; // fallback default
        if (window.catalyst && window.catalyst.userManagement) {
          try {
            const response = await window.catalyst.userManagement.getCurrentProjectUser();
            if (response && response.content && response.content.user_id) {
              userId = response.content.user_id;
            }
          } catch (err) {
            console.error('Error fetching current user:', err);
          }
        }

        var options = {};
        options.theme = "dark";
        options.service="ZohoCRM";
        options.zuid = userId;
        options.sharedBy = 15841082;
        options.group = userId;
        options.topbar={
          fullscreen :{
            hide : false
          }
        };
        options.connections={
            userAccess: {
              show: false,
              checked: false
            },
           privateOption:{
            show:false,
            checked:false
          },
          isSDPOP:true,
          hideAuthTypeDetails: {
              authType: ['oauth1', 'aws-signature','apikey','basic'],
              grantType: ['client_credentials', 'authorization_code_with_pkce']
              },
          groupName: userId,
          sharedConnections: false,
          internalConnections: false,
          customServices: false,
          hideDynamicParams: true
          };

        // Use template from API response, fallback to default if empty
        const template = challenge?.template || 'int functionName() { \n    // Write your code here\n  \n}';

        // Parse function details from template and add to options for header display
        const details = parseFunctionDetails(template);
        options.functionDetails = {
          header: true,
          id: String(challenge?.id || challenge?.questionId || ''),
          name: details.functionName,
          editArugments: {
            label: "editArgs",
            action: "add",
            align: "left",
          },
          args: details.args,
          returnType: details.returnType
        };

        const editor = window.DRE.initEditor(editorContainerRef.current,options);
        // Replace literal \n strings with actual newlines
        const formattedTemplate = template.replace(/\\n/g, '\n');
        //window.delugeEditor.setEditorContent(formattedTemplate);

        // Parse and add function parameters to auto-completion
        const params = parseFunctionParams(template);
        if (params.length > 0 && window.delugeEditor.addFuncParamsToEditor) {
          window.delugeEditor.addFuncParamsToEditor(params, false);
        }

        editorRef.current = editor;
      };

      initEditor();
    }

    return () => {
      if (editorRef.current) {
        // window.DRE.destroyEditor();
        // editorRef.current = null;
      }
    };
  }, [showEditor, challenge]);

  const editorHostProps = {};
  const EditorHost = useCallback(() => (
    <div
      ref={editorContainerRef}
      id="delugeEditorArea"
      style={{ width: '100%', height: '100%', background: '#0f131d' }}
    />
  ), []);

  const testCasesForLayout = useMemo(() => {
    // Handle array of test cases from API
    const testCasesArray = challenge?.testCases || [];

    if (Array.isArray(testCasesArray) && testCasesArray.length > 0) {
      return testCasesArray.map((tc, index) => ({
        id: tc.id || `testcase-${index}`,
        input: tc.inputValue,
        output: tc.outputValue,
        explanation: tc.explanation || '',
      }));
    }

    // Fallback for empty or invalid data
    return [{
      id: 'default',
      input: '',
      output: '',
      explanation: challenge?.question || 'Solve the challenge to capture the dinosaur.',
    }];
  }, [challenge]);

  // Handle overlay complete - save progress and navigate to map (must be before early returns)
  const handleOverlayComplete = useCallback(async () => {
    // If failure, save the failed location
    if (resultOverlayType === 'failure') {
      const currentProgress = await getGameProgress();
      const failedLocations = currentProgress.failedLocations || [];
      if (!failedLocations.includes(location?.id)) {
        const updatedProgress = {
          ...currentProgress,
          failedLocations: [...failedLocations, location?.id],
        };
        await mockApiCalls.saveProgress(updatedProgress);
      }
    }
    setShowResultOverlay(false);
    navigate('/map');
  }, [navigate, resultOverlayType, location?.id]);

  if (!location) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl text-white">Location not found</h2>
          <Button onClick={() => navigate('#/app/map')} className="mt-4">Back to Map</Button>
        </div>
      </div>
    );
  }

  const handleValidateCode = async () => {
    setValidationResult(null);
    setIsValidating(true);
    if (!editorRef.current || !window.delugeEditor) {
      setShowEditor(true);
      setValidationResult({
        type: 'error',
        title: 'Editor not ready',
        message: 'The editor is not initialized yet. Please wait a moment and try again.',
      });
      setIsValidating(false);
      return;
    }
    
    try {
      const currentCode = window.delugeEditor.getEditorValue();

      const result = await mockApiCalls.validateDelugeCode(location, currentCode);

      // Check for attemptsLeft in the response and update state
      const responseAttemptsLeft = result.attemptsLeft;
      if (responseAttemptsLeft != null) {
        setAttemptsLeft(responseAttemptsLeft);
      }

      if (result.success) {
        if(result.solved){
          const updatedProgress = {
            ...progress,
            capturedDinosaurs: [...new Set([...progress.capturedDinosaurs, location.id])],
            currentLocation: null
          };

          await mockApiCalls.saveProgress(updatedProgress);

          setValidationResult({
            type: 'success',
            title: 'Success!',
            message: `${location.dinosaur} has been captured!`,
            data: result.data,
          });

          // Store test results for overlay
          if (result.data) {
            setOverlayTestResults({
              passedTests: result.data.passedTests || result.data.totalTests,
              totalTests: result.data.totalTests,
            });
          }

          // Show success overlay and navigate to map
          setResultOverlayType('success');
          setShowResultOverlay(true);
        }
        else if(result.data){
          const testData = result.data;
          setValidationResult({
            type: 'info',
            title: 'Test Results',
            data: testData,
          });

          // Check if no attempts left and not solved - show failure overlay
          if (responseAttemptsLeft === 0) {
            // Store test results for failure overlay
            setOverlayTestResults({
              passedTests: testData.passedTests || 0,
              totalTests: testData.totalTests,
            });
            setResultOverlayType('failure');
            setShowResultOverlay(true);
          }
        } else {
          setValidationResult({
            type: 'info',
            title: 'Almost there!',
            message: result.message || 'Code validated, but the challenge is not yet solved. Please try again.',
          });

          // Check if no attempts left - show failure overlay
          if (responseAttemptsLeft === 0) {
            setResultOverlayType('failure');
            setShowResultOverlay(true);
          }
        }
      } else {
        // Check if it's a validation error
        const validationError = parseValidationError(result.message);
        if (validationError) {
          setValidationResult({
            type: 'error',
            title: 'Validation Error',
            validationError: validationError,
          });
        } else {
          setValidationResult({
            type: 'error',
            title: 'Submission Error',
            message: result.message || 'Code validation failed. Please check your solution.',
          });
        }

        // Check if no attempts left - show failure overlay
        if (responseAttemptsLeft === 0) {
          setResultOverlayType('failure');
          setShowResultOverlay(true);
        }
      }
    } catch (error) {
      const fallbackMessage = error.message || 'An error occurred during code validation.';
      setValidationResult({
        type: 'error',
        title: 'Error',
        message: fallbackMessage,
      });
    } finally {
      setShowEditor(true);
      setIsValidating(false);
    }
  };

  // Run mode - only runs non-hidden test cases, doesn't track attempts
  const handleRunCode = async () => {
    if (isValidating) return;

    setValidationResult(null);
    setIsValidating(true);

    if (!editorRef.current || !window.delugeEditor) {
      setShowEditor(true);
      setValidationResult({
        type: 'error',
        title: 'Editor not ready',
        message: 'The editor is not initialized yet. Please wait a moment and try again.',
      });
      setIsValidating(false);
      return;
    }

    try {
      const currentCode = window.delugeEditor.getEditorValue();
      const result = await mockApiCalls.runDelugeCode(location, currentCode);

      if (result.success) {
        const testData = result.data;
        const failed = testData?.failedTests || 0;

        setValidationResult({
          type: failed === 0 ? 'success' : 'info',
          title: failed === 0 ? 'All tests passed!' : 'Test Results',
          data: testData,
        });
      } else {
        // Check if it's a validation error
        const validationError = parseValidationError(result.message);
        if (validationError) {
          setValidationResult({
            type: 'error',
            title: 'Validation Error',
            validationError: validationError,
          });
        } else {
          setValidationResult({
            type: 'error',
            title: 'Run Error',
            message: result.message || 'An error occurred while running the code.',
          });
        }
      }
    } catch (error) {
      setValidationResult({
        type: 'error',
        title: 'Run Error',
        message: error.message || 'An error occurred while running the code.',
      });
    } finally {
      setShowEditor(true);
      setIsValidating(false);
    }
  };

  const handleStartCapture = () => {
    setShowEditor(true);
    setValidationResult(null);
  };

  const handleBackToMap = () => {
    navigate('/map');
  };

  if (!showEditor) {
    return (
      <div
        ref={imageRef}
        className="min-h-screen relative flex items-center justify-center text-white"
        style={{
          backgroundImage: imageSrc ? `url(${imageSrc})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80" />

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="text-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mx-auto"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-full animate-pulse"></div>
                </div>
              </div>
              <p className="text-cyan-300 mt-4 font-medium">Loading Challenge...</p>
            </div>
          </div>
        )}

        <div className="relative z-10 max-w-5xl w-full px-6 py-12">
          <button
            onClick={handleBackToMap}
            className="mb-6 inline-flex items-center gap-2 text-sm text-gray-200 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Map
          </button>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-sm uppercase tracking-wide text-cyan-200">Location</div>
                <h1 className="text-3xl font-bold">{location.name}</h1>
                <p className="text-gray-200 mt-1">{location.dinosaur}</p>
              </div>
              <div className="flex items-center gap-3">
                {isCaptured && (
                  <span className="flex items-center gap-1 text-green-300 text-sm">
                    <CheckCircle className="h-4 w-4" /> Captured
                  </span>
                )}
                <span className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-100 text-sm border border-cyan-400/30">
                  Weapon: {location.weapon}
                </span>
                {timeRemaining != null && timeRemaining > 0 && (
                  <span className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-100 text-sm border border-amber-400/30">
                    <span>⏱️</span>
                    <span>{formatTimeRemaining(timeRemaining)}</span>
                  </span>
                )}
                {isTimeEnded && (
                  <span className="flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/20 text-red-100 text-sm border border-red-400/30">
                    <span>⏱️</span>
                    <span>Time's Up!</span>
                  </span>
                )}
              </div>
            </div>

            <p className="mt-4 text-gray-200 whitespace-pre-wrap">{(location.description || challenge?.question || '').replace(/\\n/g, '\n')}</p>

            <div className="mt-6 flex flex-wrap gap-3 items-center">
              <Button
                onClick={handleBackToMap}
                className="bg-gray-800 hover:bg-gray-700 text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Map
              </Button>
              <Button
                onClick={handleStartCapture}
                disabled={isCaptured || isLoading || isTimeEnded || !isEditorReady}
                className={`bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-900/40 ${
                  isCaptured || isLoading || isTimeEnded || !isEditorReady ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                {!isEditorReady ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading Editor...
                  </>
                ) : (
                  <>
                    <Code className="h-4 w-4 mr-2" />
                    {isCaptured ? 'Already Captured' : isTimeEnded ? "Time's Up" : 'Capture'}
                  </>
                )}
              </Button>
              {!isEditorReady && (
                <span className="text-sm text-gray-400">Preparing code editor...</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100vw', height: '100vh' }}>
      <CodingGameSplitLayout
        questionTitle={location.name || 'Challenge'}
        questionBody={<div style={{ whiteSpace: 'pre-wrap' }}>{(challenge?.question || 'No question loaded yet.').replace(/\\n/g, '\n')}</div>}
        testCases={testCasesForLayout}
        EditorComponent={EditorHost}
        editorProps={editorHostProps}
        onRun={handleRunCode}
        onSubmit={handleValidateCode}
        onBack={handleBackToMap}
        languageOptions={['Deluge']}
        selectedLanguage={'Deluge'}
        output={validationResult}
        isRunning={isValidating}
        timeRemaining={timeRemaining}
        onTimeUp={handleTimeUp}
        attemptsLeft={attemptsLeft}
        maxAttempts={maxAttempts}
      />

      {/* Result overlay for success/failure animations */}
      {showResultOverlay && (
        <ResultOverlay
          type={resultOverlayType}
          dinosaurName={location.dinosaur}
          dinosaurImage={location.dinosaurImage}
          weapon={location.weapon}
          testResults={overlayTestResults}
          onComplete={handleOverlayComplete}
          autoNavigateDelay={7000}
        />
      )}
    </div>
  );
};

export default LocationChallenge;
